import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PagedData from "../../model/PagedData";
import AnnexData from "../../model/AnnexData";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import TimeUtil from "../../utils/TimeUtil";
import AppIcons from "../../utils/AppIcons";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import CHElement from "../../model/CHElement";
import e3dModel from "../../model/e3dModel";
import { WebViewDTO, WebViewResponseItem } from "../../model/WebViewDTO";

function AnnexDataChemiTools() {
  // read the ID from the url
  const { chid, e3did } = useParams();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // reference to the navigator
  const navigate = useNavigate();
  // store the information
  const [data, setData] = useState<AnnexData[]>([]);
  // store information about the parent entity
  const [parentData, setParentData] = useState<
    CHElement | e3dModel | undefined
  >(undefined);
  // manage data loading animation
  const [loading, setLoading] = useState(true);
  // settings received from the tools
  const [webViewInfo, setWebViewInfo] = useState<WebViewDTO | undefined>(
    undefined,
  );

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        // load selected information that has been injected by the tool
        const webViewData = localStorage.getItem("web_view_dto");
        let auxWebViewInfo: WebViewDTO | undefined = undefined;
        if (webViewData) {
          auxWebViewInfo = JSON.parse(webViewData);
          setWebViewInfo(auxWebViewInfo);
        }
        // prepare the call to get the documents list
        const aux_chid = chid ? parseInt(chid) : undefined;
        const aux_e3did = e3did ? parseInt(e3did) : undefined;
        const pagedData = await apiClient?.get<PagedData<AnnexData[]>>({
          url: APIEndPoints.ANNEXDATA.ALL(aux_chid, undefined, aux_e3did),
        });
        // get information about the parent entity: CH Asset / e3D
        let parentInfo = undefined;
        if (aux_chid) {
          parentInfo = await apiClient?.get<CHElement>({
            url: APIEndPoints.CHELEMENTS.ONE(aux_chid),
          });
        } else if (aux_e3did) {
          parentInfo = await apiClient?.get<CHElement>({
            url: APIEndPoints.E3DS.ONE(aux_e3did),
          });
        }
        setData(pagedData.items);
        setParentData(parentInfo);
        setLoading(false);
      } catch (ex) {}
    };
    getAllData();
  }, [apiClient, chid, e3did, navigate]);

  const onItemSelect = (id: number) => {
    const copy = JSON.parse(JSON.stringify(webViewInfo)) as WebViewDTO;
    const selectedItems = new Set<number>(copy.selectedItemsIDs);
    if (selectedItems.has(id)) {
      selectedItems.delete(id);
    } else {
      selectedItems.add(id);
    }
    copy.selectedItemsIDs = Array.from(selectedItems);
    setWebViewInfo(copy);
  };

  const prepareResponse = (): string => {
    const copy = JSON.parse(JSON.stringify(webViewInfo)) as WebViewDTO;
    const selectedItems = new Set<number>(copy.selectedItemsIDs);
    // go through all the documents and populate ID and NAME
    copy.selectedItemsInfo = [];
    for (let i = 0; i < data.length; i++) {
      if (selectedItems.has(data[i].id!)) {
        copy.selectedItemsInfo.push(
          new WebViewResponseItem(data[i].id!, data[i].title),
        );
      }
    }
    return encodeURIComponent(JSON.stringify(copy));
  };

  return (
    <div className="CT-page">
      <h1>
        <span>List of documents for:</span>
        <br />
        {parentData?.name}
      </h1>
      <div className="CT-container">
        {data.map((item) => {
          const selectedItems = new Set(webViewInfo?.selectedItemsIDs);
          const buttonAction = selectedItems.has(item.id!)
            ? ButtonAction.CHECKED_YES
            : ButtonAction.CHECKED_NO;
          const buttonLabel = selectedItems.has(item.id!)
            ? "Selected"
            : "Select";
          const css = selectedItems.has(item.id!)
            ? "CT-selected"
            : "CT-unselected";

          return (
            <div className="CT-document">
              <h2>{item.title}</h2>
              <div className="CT-document-details">
                <div>
                  {AppIcons.FILE}
                  {item.docs?.length}{" "}
                  {item.docs?.length == 1 ? "file" : "files"}
                </div>
                <div>
                  {AppIcons.CLOCK}
                  {TimeUtil.display_date(item.timestamp)}
                </div>
                <div>
                  {AppIcons.USER}
                  {item.username}
                </div>
              </div>
              {EmbeddingToolInfo.isChemiAnalysis() && (
                <div className="flex items-start mt-5">
                  <Button
                    label={buttonLabel}
                    className={"CT-open-button " + css}
                    purpose={buttonAction}
                    onClick={() => {
                      onItemSelect(item.id!);
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="CT-buttons">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => {
            const payload = "unity:popup_action=close";
            if (EmbeddingToolInfo.isToolOnWindows()) {
              // @ts-ignore
              uwb.ExecuteJsMethod("JSCallback", payload);
            } else {
              window.location.href = payload;
            }
          }}
        />
        <Button
          label="Save changes"
          purpose={ButtonAction.USE}
          onClick={() => {
            const payload = "unity:selection=" + prepareResponse();
            if (EmbeddingToolInfo.isToolOnWindows()) {
              // @ts-ignore
              uwb.ExecuteJsMethod("JSCallback", payload);
            } else {
              window.location.href = payload;
            }
          }}
        />
      </div>
    </div>
  );
}

export default AnnexDataChemiTools;
